import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mediaApi } from './api';
import { FocalPointPicker } from './FocalPointPicker';
import { keys } from '@/lib/query/keys';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { useToast } from '@/lib/ui';
import { Button } from '@/lib/ui/Button';
import { Dialog } from '@/lib/ui/Dialog';
import { derivativeUrl } from '@/lib/media/derivatives';
import type { FocalPoint, MediaAsset } from '@/lib/api/types';

/**
 * "Focal point" beside a chosen photo in any form (round 8, R8-05): opens the
 * picker on that asset without leaving the record. The point belongs to the
 * photo, not to the record — so it is saved on the asset at once, and every
 * page that shows the photo follows.
 */
export function FocalPointControl({ id, disabled }: { id: string; disabled?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { allowed: canManage } = useGate('media:manage');
  const asset = useQuery({
    queryKey: keys.media.detail(id),
    queryFn: () => mediaApi.get(id),
    staleTime: 60_000,
  });
  const a = asset.data;
  if (!a || a.kind !== 'image' || a.is_restricted) return null;
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled || !canManage}
        title={canManage ? undefined : t('media.needsManage')}
        onClick={() => setOpen(true)}
      >
        {a.focal ? t('media.focal.edit') : t('media.focal.set')}
      </Button>
      {open && <FocalPointDialog asset={a} onClose={() => setOpen(false)} />}
    </>
  );
}

function FocalPointDialog({ asset, onClose }: { asset: MediaAsset; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();
  const [focal, setFocal] = useState<FocalPoint | null>(asset.focal ?? null);
  const save = useMutation({
    mutationFn: () => mediaApi.update(asset.id, { focal }),
    onSuccess: (updated) => {
      qc.setQueryData(keys.media.detail(asset.id), updated);
      void qc.invalidateQueries({ queryKey: keys.media.all() });
      toast.success(t('media.focal.savedToast'), t('media.focal.savedBody'));
      onClose();
    },
    onError: (e) => onError(e),
  });
  const unchanged =
    (focal?.x ?? null) === (asset.focal?.x ?? null) && (focal?.y ?? null) === (asset.focal?.y ?? null);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('media.focal.dialogTitle', { name: asset.file_name })}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            {t('common.cancel')}
          </Button>
          <Button loading={save.isPending} disabled={unchanged} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FocalPointPicker
        src={derivativeUrl(asset, 'card') ?? asset.url}
        alt={asset.alt.en}
        value={focal}
        onChange={setFocal}
      />
    </Dialog>
  );
}
